import { MXP365_API_BASE_URL } from '../config-client';

const CACHE_TTL = 30000; // 30 seconds

interface NotificationCountResponse {
  notificationsUnreadCount: number;
  chatMessagesUnreadCount: number;
}

interface CacheEntry {
  notifications: number | undefined;
  chatMessages: number | undefined;
  timestamp: number;
}

interface NotificationStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => CacheEntry | null;
  getServerSnapshot: () => CacheEntry | null;
}

// Cache store instances by token
const storeCache = new Map<string, NotificationStore>();

export const createNotificationStore = (token: string): NotificationStore => {
  // Return existing store if available
  const existing = storeCache.get(token);

  if (existing) {
    return existing;
  }

  let cache: CacheEntry | null = null;
  let activePromise: Promise<void> | null = null;
  let intervalId: NodeJS.Timeout | null = null;
  const listeners = new Set<() => void>();

  const fetchNotificationCount =
    async (): Promise<NotificationCountResponse | null> => {
      if (typeof token === 'undefined') {
        return null;
      }

      const response = await fetch(
        `${MXP365_API_BASE_URL}/api/ext/notifications/unread-count`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch notification count: ${response.status}`,
        );
      }

      return response.json();
    };

  const fetchData = async () => {
    // If already fetching, return existing promise
    if (activePromise) return activePromise;

    // Start new fetch
    activePromise = (async () => {
      try {
        const data = await fetchNotificationCount();

        cache = {
          notifications: data?.notificationsUnreadCount,
          chatMessages: data?.chatMessagesUnreadCount,
          timestamp: Date.now(),
        };

        listeners.forEach(listener => listener());
      } catch {
        cache = {
          notifications: undefined,
          chatMessages: undefined,
          timestamp: Date.now(),
        };

        listeners.forEach(listener => listener());
      } finally {
        activePromise = null;
      }
    })();

    return activePromise;
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);

    // Start interval when first subscriber joins
    if (listeners.size === 1 && !intervalId) {
      // Initial fetch
      if (!cache || cache.timestamp + CACHE_TTL < Date.now()) {
        fetchData();
      }

      // Set up auto-refresh every 30 seconds
      intervalId = setInterval(() => {
        fetchData();
      }, 30000);
    }

    return () => {
      listeners.delete(listener);

      // Stop interval when last subscriber leaves
      if (listeners.size === 0 && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  };

  const getSnapshot = () => cache;

  const store: NotificationStore = {
    subscribe,
    getSnapshot,
    getServerSnapshot: () => null,
  };

  // Cache this store instance
  storeCache.set(token, store);

  return store;
};
